/**
 * Delivery contracts (M5 task 5; 03-modules.md §14, 04-data-model.md "delivery", 05-api.md
 * "Delivery", 06-flows.md F4 steps 2 and 5-8, 07-connectors.md pidge / borzo / google-maps).
 *
 * Own-channel delivery orders are handed to a courier partner (Pidge, Borzo) found through the
 * outlet's `delivery` provider route; vendor formats stop at the connectors (CLAUDE.md rule 9).
 * This file holds what the module serves and takes: zones, slots, settings, the serviceability
 * check, the order's delivery request (drop address, charge, slot), courier quotes, deliveries
 * with their timeline, and the events.
 *
 * Personal data: the drop name, phone and address are in API responses (staff and the diner's own
 * app) only. Events and realtime rooms carry none, and never an OTP.
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema, paginated, pageQuerySchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema, paiseSchema } from './money.js';
import { geoPointSchema, sortSchema, timeOfDaySchema } from './org.js';
import { phoneSchema } from './identity.js';

// ---------------------------------------------------------------------------
// Vocabulary

export const DELIVERY_STATUS_LIST = [
  'requested',
  'pending',
  'assigned',
  'arriving',
  'picked_up',
  'delivered',
  'failed',
  'returned',
  'cancelled',
  'create_failed',
] as const;
export type DeliveryStatusValue = (typeof DELIVERY_STATUS_LIST)[number];
export const deliveryStatusValueSchema = z.enum(DELIVERY_STATUS_LIST);

/** What a courier reports (the connector SDK's standard vocabulary). */
export const PARTNER_STATUS_LIST = [
  'pending',
  'assigned',
  'arriving',
  'picked_up',
  'delivered',
  'failed',
  'cancelled',
] as const;
export type PartnerStatusValue = (typeof PARTNER_STATUS_LIST)[number];

export const DELIVERY_STATUS_LABEL: Readonly<Record<DeliveryStatusValue, string>> = {
  requested: 'Booking',
  pending: 'Finding a rider',
  assigned: 'Rider assigned',
  arriving: 'Rider on the way to you',
  picked_up: 'Out for delivery',
  delivered: 'Delivered',
  failed: 'Delivery failed',
  returned: 'Returned',
  cancelled: 'Cancelled',
  create_failed: 'Could not book',
};

export const PICK_RULE_LIST = ['cheapest', 'preferred'] as const;
export const pickRuleSchema = z.enum(PICK_RULE_LIST);
export type PickRuleValue = (typeof PICK_RULE_LIST)[number];

export const DISTANCE_BASIS_LIST = ['straight_line', 'road'] as const;
export const distanceBasisSchema = z.enum(DISTANCE_BASIS_LIST);

export const QUOTE_OUTCOME_LIST = ['quoted', 'failed', 'timeout', 'skipped'] as const;
export const quoteOutcomeSchema = z.enum(QUOTE_OUTCOME_LIST);

export const CANCEL_STATE_LIST = ['none', 'requested', 'done', 'failed'] as const;
export const cancelStateSchema = z.enum(CANCEL_STATE_LIST);

export const SERVICEABILITY_REASON_LIST = [
  'no_zones',
  'no_outlet_location',
  'outside_zone',
  'below_minimum_order',
  'address_not_located',
  'no_courier',
] as const;
export const serviceabilityReasonSchema = z.enum(SERVICEABILITY_REASON_LIST);

export const SERVICEABILITY_MESSAGE: Readonly<
  Record<(typeof SERVICEABILITY_REASON_LIST)[number], string>
> = {
  no_zones: 'This outlet has not set up delivery areas yet.',
  no_outlet_location: 'The outlet has no map location set, so a radius area cannot be checked.',
  outside_zone: 'Sorry, we do not deliver to that address.',
  below_minimum_order: 'The order is below the minimum for that area.',
  address_not_located:
    'We could not find that address on the map. Add a landmark or pick it on the map.',
  no_courier: 'No delivery partner can take this address right now.',
};

/** Slots a day can hold, quote and OTP bounds. */
export const MAX_ZONES_PER_OUTLET = 50;
export const MAX_SLOTS_PER_OUTLET = 300;
export const QUOTE_BUDGET_MS = 3_000;
export const QUOTE_BUDGET_MIN_MS = 500;

const archivable = {
  archivedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
};

// ---------------------------------------------------------------------------
// Zones

/** GeoJSON position, [longitude, latitude]. */
const positionSchema = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
/** A GeoJSON Polygon. Closure, area and crossings are checked by the domain (`polygonProblems`). */
export const geoPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(positionSchema).min(4).max(500)).min(1).max(5),
});
export type GeoPolygonValue = z.output<typeof geoPolygonSchema>;

const zoneName = z.string().trim().min(1).max(60);
const radiusSchema = z.int().min(100).max(100_000);
const zonePriceFields = {
  /** Charged for any address in the zone. */
  basePaise: nonNegativePaiseSchema,
  /** Added for each started kilometre from the outlet. */
  perKmPaise: nonNegativePaiseSchema,
  /** Items below this are refused in the zone (0 = no minimum). */
  minOrderPaise: nonNegativePaiseSchema,
};

export const deliveryZoneSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: zoneName,
  /** A circle around the outlet, metres; null for a polygon zone. */
  radiusM: radiusSchema.nullable(),
  /** The area drawn on the map; null for a radius zone. */
  polygon: geoPolygonSchema.nullable(),
  ...zonePriceFields,
  sort: sortSchema,
  ...archivable,
});
export type DeliveryZoneView = z.output<typeof deliveryZoneSchema>;
export const deliveryZoneListSchema = z.object({ data: z.array(deliveryZoneSchema) });

export const createDeliveryZoneSchema = z
  .object({
    id: uuidSchema.optional(),
    name: zoneName,
    radiusM: radiusSchema.optional(),
    polygon: geoPolygonSchema.optional(),
    basePaise: zonePriceFields.basePaise,
    perKmPaise: zonePriceFields.perKmPaise,
    minOrderPaise: zonePriceFields.minOrderPaise.optional(),
    sort: sortSchema.optional(),
  })
  .refine((v) => (v.radiusM === undefined) !== (v.polygon === undefined), {
    message: 'A zone is either a radius or a polygon, not both',
    path: ['radiusM'],
  });
export const updateDeliveryZoneSchema = z
  .object({
    name: zoneName,
    /** Set a radius and send `polygon: null` to turn a polygon zone into a radius zone. */
    radiusM: radiusSchema.nullable(),
    polygon: geoPolygonSchema.nullable(),
    basePaise: zonePriceFields.basePaise,
    perKmPaise: zonePriceFields.perKmPaise,
    minOrderPaise: zonePriceFields.minOrderPaise,
    sort: sortSchema,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });
export const deliveryZoneParamsSchema = z.object({ outletId: uuidSchema, zoneId: uuidSchema });
export const deliveryZoneQuerySchema = z.object({
  includeArchived: z.enum(['true', 'false']).optional(),
});

// ---------------------------------------------------------------------------
// Slots

const dayOfWeekSchema = z.int().min(1).max(7);
const capacitySchema = z.int().min(1).max(500);

export const deliverySlotSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  /** ISO weekday, 1 = Monday. */
  dayOfWeek: dayOfWeekSchema,
  startTime: timeOfDaySchema,
  endTime: timeOfDaySchema,
  /** Deliveries that can be booked into the slot on one date. */
  capacity: capacitySchema,
  ...archivable,
});
export type DeliverySlotView = z.output<typeof deliverySlotSchema>;
export const deliverySlotListSchema = z.object({ data: z.array(deliverySlotSchema) });

const slotInput = z.object({
  id: uuidSchema.optional(),
  dayOfWeek: dayOfWeekSchema,
  startTime: timeOfDaySchema,
  endTime: timeOfDaySchema,
  capacity: capacitySchema,
});
/**
 * `PUT {o}/delivery-slots`: the slots to keep. With an `id` a slot is changed, without one it is
 * created; with `replace: true` the live slots not listed are archived.
 */
export const upsertDeliverySlotsSchema = z.object({
  slots: z.array(slotInput).max(MAX_SLOTS_PER_OUTLET),
  replace: z.boolean().optional(),
});
export const deliverySlotParamsSchema = z.object({ outletId: uuidSchema, slotId: uuidSchema });

export const slotAvailabilityQuerySchema = z.object({ date: businessDateSchema });
export const slotAvailabilitySchema = z.object({
  date: businessDateSchema,
  slots: z.array(
    z.object({
      slotId: uuidSchema,
      startTime: timeOfDaySchema,
      endTime: timeOfDaySchema,
      capacity: z.int(),
      booked: z.int(),
      open: z.boolean(),
      full: z.boolean(),
      past: z.boolean(),
    }),
  ),
});
export type SlotAvailabilityView = z.output<typeof slotAvailabilitySchema>;

// ---------------------------------------------------------------------------
// Settings

export const DELIVERY_SETTING_DEFAULTS = {
  pickRule: 'cheapest',
  quoteTimeoutMs: QUOTE_BUDGET_MS,
  markupPaise: 0n,
  distanceBasis: 'straight_line',
  slotLeadMinutes: 30,
  pickupPhone: null,
} as const;

export const deliverySettingSchema = z.object({
  outletId: uuidSchema,
  /** `cheapest` offers the lowest quote first; `preferred` the first courier of the route that answers. */
  pickRule: pickRuleSchema,
  /** How long each courier has to quote (500 to 3000 ms). */
  quoteTimeoutMs: z.int().min(QUOTE_BUDGET_MIN_MS).max(QUOTE_BUDGET_MS),
  /** Added to the zone's charge on every delivery. */
  markupPaise: nonNegativePaiseSchema,
  /** The distance the per-km charge uses: straight line, or the road distance from maps. */
  distanceBasis: distanceBasisSchema,
  /** A slot starting sooner than this from now cannot be booked. */
  slotLeadMinutes: z.int().min(0).max(1440),
  /** The number the courier calls at the outlet when picking up; deliveries cannot be booked without it. */
  pickupPhone: z.string().nullable(),
  /** False until someone saved the settings: these are the defaults. */
  isDefault: z.boolean(),
  updatedAt: isoTimestampSchema.nullable(),
  version: z.int(),
});
export type DeliverySettingView = z.output<typeof deliverySettingSchema>;
export const saveDeliverySettingSchema = z
  .object({
    pickRule: pickRuleSchema,
    quoteTimeoutMs: deliverySettingSchema.shape.quoteTimeoutMs,
    markupPaise: nonNegativePaiseSchema,
    distanceBasis: distanceBasisSchema,
    slotLeadMinutes: deliverySettingSchema.shape.slotLeadMinutes,
    pickupPhone: phoneSchema.nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

// ---------------------------------------------------------------------------
// Serviceability (staff and the customer app)

export const dropAddressSchema = z.object({
  /** The address as typed or picked (what the rider reads). */
  text: z.string().trim().min(5).max(400),
  landmark: z.string().trim().max(120).optional(),
  pincode: z
    .string()
    .trim()
    .regex(/^[1-9]\d{5}$/, { message: 'Expected a 6-digit PIN code' })
    .optional(),
  /** The map position when the app has it (a picked place); else the server finds it. */
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
});
export type DropAddress = z.output<typeof dropAddressSchema>;

export const serviceabilityRequestSchema = z.object({
  address: dropAddressSchema,
  /** The items total, to check the zone's minimum order. */
  orderValuePaise: nonNegativePaiseSchema.optional(),
  /** Also ask the couriers whether they can take it (default yes). */
  checkCouriers: z.boolean().optional(),
});

export const serviceabilityViewSchema = z.object({
  serviceable: z.boolean(),
  reason: serviceabilityReasonSchema.nullable(),
  /** Something to show the customer. */
  message: z.string().nullable(),
  location: geoPointSchema.nullable(),
  /** The position was found from the address text (not given). */
  geocoded: z.boolean(),
  zone: z.object({ id: uuidSchema, name: z.string() }).nullable(),
  distanceM: z.int(),
  distanceBasis: distanceBasisSchema,
  /** What the customer pays for delivery: the zone's charge plus the outlet's markup. */
  chargePaise: nonNegativePaiseSchema,
  minOrderPaise: nonNegativePaiseSchema,
  couriers: z.object({
    /** The outlet has delivery partners on its route. */
    configured: z.boolean(),
    /** The partners were asked (false when the outlet has none, or no pickup phone to give them). */
    checked: z.boolean(),
    /** Partners that quoted, with their time estimate; never their price. */
    available: z.array(z.object({ provider: z.string(), etaMinutes: z.int().nullable() })),
  }),
});
export type ServiceabilityView = z.output<typeof serviceabilityViewSchema>;

// ---------------------------------------------------------------------------
// Maps (autocomplete and geocoding through the outlet's maps connector)

export const autocompleteQuerySchema = z.object({ input: z.string().trim().min(2).max(200) });
export const autocompleteViewSchema = z.object({
  data: z.array(z.object({ placeRef: z.string(), description: z.string() })),
});
export const geocodeRequestSchema = z.object({ address: z.string().trim().min(3).max(400) });
export const geocodeViewSchema = z.object({
  data: z.array(
    z.object({
      formattedAddress: z.string(),
      location: geoPointSchema,
      pincode: z.string().nullable(),
    }),
  ),
});

// ---------------------------------------------------------------------------
// The order's delivery request (drop details, charge, slot)

export const deliveryRequestSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  orderId: uuidSchema,
  orderNo: z.int().nullable(),
  /** Null once the customer was erased (DPDP): the delivery's money and times stay. */
  dropName: z.string().nullable(),
  dropPhone: z.string().nullable(),
  address: z.string().nullable(),
  landmark: z.string().nullable(),
  pincode: z.string().nullable(),
  location: geoPointSchema.nullable(),
  zone: z.object({ id: uuidSchema, name: z.string() }).nullable(),
  distanceM: z.int(),
  distanceBasis: distanceBasisSchema,
  /** The delivery charge on the customer's bill. */
  chargePaise: nonNegativePaiseSchema,
  slot: z
    .object({
      slotId: uuidSchema,
      date: businessDateSchema,
      startTime: timeOfDaySchema,
      endTime: timeOfDaySchema,
    })
    .nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type DeliveryRequestView = z.output<typeof deliveryRequestSchema>;

/** `PUT {o}/orders/{id}/delivery-request`: where it goes, who gets it, and when. */
export const setDeliveryRequestSchema = z.object({
  id: uuidSchema.optional(),
  name: z.string().trim().min(1).max(80),
  phone: phoneSchema,
  address: dropAddressSchema,
  /** Book a slot (the order's scheduled time becomes the slot's start); omit for as soon as possible. */
  slot: z.object({ slotId: uuidSchema, date: businessDateSchema }).nullable().optional(),
});
export const orderDeliveryParamsSchema = z.object({ outletId: uuidSchema, orderId: uuidSchema });

// ---------------------------------------------------------------------------
// Quotes

export const deliveryQuoteSchema = z.object({
  id: uuidSchema,
  requestId: uuidSchema,
  batchId: uuidSchema,
  /** The connector key: `pidge`, `borzo`. */
  provider: z.string(),
  connectorInstallId: uuidSchema,
  /** Position in the outlet's route; 0 is the most preferred. */
  routeIndex: z.int(),
  outcome: quoteOutcomeSchema,
  amountPaise: nonNegativePaiseSchema.nullable(),
  etaMinutes: z.int().nullable(),
  validUntil: isoTimestampSchema.nullable(),
  /** Why a courier gave no quote (`TIMEOUT`, `REJECTED`, `CIRCUIT_OPEN`, …). */
  errorCode: z.string().nullable(),
  durationMs: z.int(),
  /** The one the outlet's rule picked. */
  picked: z.boolean(),
  /** Still usable to book (answered, not lapsed). */
  usable: z.boolean(),
  createdAt: isoTimestampSchema,
});
export type DeliveryQuoteView = z.output<typeof deliveryQuoteSchema>;

export const quoteRoundSchema = z.object({
  requestId: uuidSchema,
  batchId: uuidSchema,
  rule: pickRuleSchema,
  /** Best first: the pick, the other quotes cheapest first, then the couriers that did not answer. */
  quotes: z.array(deliveryQuoteSchema),
  pickedQuoteId: uuidSchema.nullable(),
  /** The outlet has delivery partners on its route. */
  configured: z.boolean(),
});
export type QuoteRoundView = z.output<typeof quoteRoundSchema>;
export const requestQuotesSchema = z.object({
  /** When the food will be ready, so the courier can plan the pickup. Default: now. */
  readyAt: isoTimestampSchema.optional(),
});

// ---------------------------------------------------------------------------
// Deliveries

const riderSchema = z.object({ name: z.string(), phone: z.string().nullable() });
export const deliveryEventKindSchema = z.string();

export const deliverySchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  orderId: uuidSchema,
  orderNo: z.int().nullable(),
  requestId: uuidSchema,
  provider: z.string(),
  connectorInstallId: uuidSchema,
  /** The courier's own reference for the booking. */
  providerRef: z.string().nullable(),
  status: deliveryStatusValueSchema,
  rider: riderSchema.nullable(),
  riderLocation: z.object({ lat: z.number(), lng: z.number(), at: isoTimestampSchema }).nullable(),
  /** What the courier quoted when it was booked. */
  quotePaise: nonNegativePaiseSchema.nullable(),
  /** What the courier actually billed, once known (reconciliation). */
  actualCostPaise: nonNegativePaiseSchema.nullable(),
  cancellationFeePaise: nonNegativePaiseSchema.nullable(),
  /** The delivery charge the customer paid on the bill. */
  chargeToCustomerPaise: nonNegativePaiseSchema,
  /** The code the rider shows at the outlet. */
  pickupOtp: z.string().nullable(),
  /** The customer's code exists (it is never shown to staff). */
  dropOtpIssued: z.boolean(),
  pickupVerifiedAt: isoTimestampSchema.nullable(),
  dropVerifiedAt: isoTimestampSchema.nullable(),
  cancelState: cancelStateSchema,
  cancelReason: z.string().nullable(),
  cancelError: z.string().nullable(),
  createError: z.string().nullable(),
  failureReason: z.string().nullable(),
  returnedAt: isoTimestampSchema.nullable(),
  returnNote: z.string().nullable(),
  requestedAt: isoTimestampSchema,
  pickedUpAt: isoTimestampSchema.nullable(),
  deliveredAt: isoTimestampSchema.nullable(),
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type DeliveryView = z.output<typeof deliverySchema>;

export const deliveryEventSchema = z.object({
  id: uuidSchema,
  deliveryId: uuidSchema,
  kind: z.string(),
  fromStatus: deliveryStatusValueSchema.nullable(),
  toStatus: deliveryStatusValueSchema.nullable(),
  /** Staff, the courier (webhook) or the system. */
  source: z.enum(['staff', 'partner', 'system']),
  note: z.string().nullable(),
  at: isoTimestampSchema,
});
export type DeliveryEventView = z.output<typeof deliveryEventSchema>;

export const deliveryBoardItemSchema = deliverySchema.extend({
  dropName: z.string().nullable(),
  address: z.string().nullable(),
});
export type DeliveryBoardItem = z.output<typeof deliveryBoardItemSchema>;

export const deliveryDetailSchema = deliverySchema.extend({
  request: deliveryRequestSchema,
  events: z.array(deliveryEventSchema),
});
export type DeliveryDetailView = z.output<typeof deliveryDetailSchema>;

/** `GET {o}/orders/{id}/delivery`: everything the order screen needs. */
export const orderDeliverySchema = z.object({
  request: deliveryRequestSchema.nullable(),
  /** The live delivery, else the latest one. */
  delivery: deliveryDetailSchema.nullable(),
  /** Deliveries before it (cancelled, failed to book). */
  history: z.array(deliverySchema),
  /** The latest quote round. */
  quotes: quoteRoundSchema.nullable(),
  /** Booking is possible: a request exists and no live delivery does. */
  canBook: z.boolean(),
});
export type OrderDeliveryView = z.output<typeof orderDeliverySchema>;

export const createDeliverySchema = z.object({
  id: uuidSchema.optional(),
  quoteId: uuidSchema,
});
export const cancelDeliverySchema = z.object({ reason: z.string().trim().min(1).max(200) });
export const verifyOtpSchema = z.object({ otp: z.string().trim().min(3).max(12) });
/**
 * The answer to a code check. A wrong code is an ANSWER, not an error (200, `verified: false`): it is counted, and
 * the answer is stored with the Idempotency-Key like any other, so a retried wrong code is not counted twice.
 * `locked` once too many wrong codes were tried: even the right code is refused then.
 */
export const verifyOtpResultSchema = z.object({
  verified: z.boolean(),
  locked: z.boolean(),
  /** Wrong tries left before the code locks. */
  attemptsLeft: z.int().min(0),
  delivery: z.lazy(() => deliveryDetailSchema),
});
export type VerifyOtpResult = z.output<typeof verifyOtpResultSchema>;
export const markReturnedSchema = z.object({ note: z.string().trim().max(300).optional() });
export const deliveryParamsSchema = z.object({ outletId: uuidSchema, deliveryId: uuidSchema });

/** `?status=assigned,picked_up` (comma-separated delivery statuses), `?orderId=`. */
export const deliveryListQuerySchema = pageQuerySchema.extend({
  status: z.string().max(200).optional(),
  orderId: uuidSchema.optional(),
});
/** The statuses named in `?status=`; unknown names are an error for the caller to report. */
export function parseStatusFilter(value: string | undefined): {
  statuses: DeliveryStatusValue[];
  unknown: string[];
} {
  const names = (value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
  const known = new Set<string>(DELIVERY_STATUS_LIST);
  return {
    statuses: names.filter((n) => known.has(n)) as DeliveryStatusValue[],
    unknown: names.filter((n) => !known.has(n)),
  };
}
export const deliveryListSchema = paginated(deliveryBoardItemSchema);

// ---------------------------------------------------------------------------
// Events. No personal data and no OTPs: they are projected to the outlet-wide pos room.

const ref = {
  deliveryId: uuidSchema,
  orderId: uuidSchema,
  orderNo: z.int().nullable(),
  provider: z.string(),
};

export const deliveryZoneSavedV1 = defineEvent(
  'delivery.zone_saved',
  1,
  z.object({ zoneId: uuidSchema, change: z.enum(['created', 'updated', 'archived']) }),
);
export const deliverySlotsSavedV1 = defineEvent(
  'delivery.slots_saved',
  1,
  z.object({ created: z.int(), updated: z.int(), archived: z.int() }),
);
export const deliverySettingsSavedV1 = defineEvent(
  'delivery.settings_saved',
  1,
  z.object({ pickRule: pickRuleSchema, quoteTimeoutMs: z.int() }),
);

export const deliveryRequestSavedV1 = defineEvent(
  'delivery.request_saved',
  1,
  z.object({
    requestId: uuidSchema,
    orderId: uuidSchema,
    zoneId: uuidSchema.nullable(),
    chargePaise: paiseSchema,
    distanceM: z.int(),
  }),
);

export const deliveryQuotedV1 = defineEvent(
  'delivery.quoted',
  1,
  z.object({
    requestId: uuidSchema,
    orderId: uuidSchema,
    batchId: uuidSchema,
    quoted: z.int(),
    failed: z.int(),
    pickedProvider: z.string().nullable(),
    pickedPaise: paiseSchema.nullable(),
  }),
);

/** A delivery was asked for; the worker books it with the courier next. */
export const deliveryCreatedV1 = defineEvent(
  'delivery.created',
  1,
  z.object({
    ...ref,
    quotePaise: paiseSchema.nullable(),
    chargeToCustomerPaise: paiseSchema,
  }),
);

export const deliveryStatusChangedV1 = defineEvent(
  'delivery.status_changed',
  1,
  z.object({
    ...ref,
    from: deliveryStatusValueSchema,
    to: deliveryStatusValueSchema,
    source: z.enum(['staff', 'partner', 'system']),
    riderName: z.string().nullable(),
  }),
);

/** Booking failed, or the courier could not deliver. */
export const deliveryFailedV1 = defineEvent(
  'delivery.failed',
  1,
  z.object({
    ...ref,
    stage: z.enum(['booking', 'delivery']),
    reason: z.string().nullable(),
  }),
);

export const deliveryReturnedV1 = defineEvent(
  'delivery.returned',
  1,
  z.object({ ...ref, note: z.string().nullable() }),
);

export const deliveryCancelRequestedV1 = defineEvent(
  'delivery.cancel_requested',
  1,
  z.object({ ...ref, reason: z.string() }),
);

/** The courier answered the cancel (or refused it). */
export const deliveryCancelRecordedV1 = defineEvent(
  'delivery.cancel_recorded',
  1,
  z.object({
    ...ref,
    outcome: z.enum(['cancelled', 'refused']),
    feePaise: paiseSchema.nullable(),
  }),
);

export const deliveryLocationUpdatedV1 = defineEvent(
  'delivery.location_updated',
  1,
  z.object({
    deliveryId: uuidSchema,
    orderId: uuidSchema,
    lat: z.number(),
    lng: z.number(),
    at: isoTimestampSchema,
  }),
);

export const deliveryCostRecordedV1 = defineEvent(
  'delivery.cost_recorded',
  1,
  z.object({
    ...ref,
    actualCostPaise: paiseSchema,
    chargeToCustomerPaise: paiseSchema,
  }),
);

/** A pickup or drop code was checked (right, or wrong and counted). Never carries the code. */
export const deliveryCodeCheckedV1 = defineEvent(
  'delivery.code_checked',
  1,
  z.object({ ...ref, which: z.enum(['pickup', 'drop']), verified: z.boolean() }),
);

export const DELIVERY_EVENTS = [
  deliveryZoneSavedV1,
  deliverySlotsSavedV1,
  deliverySettingsSavedV1,
  deliveryRequestSavedV1,
  deliveryQuotedV1,
  deliveryCreatedV1,
  deliveryStatusChangedV1,
  deliveryFailedV1,
  deliveryReturnedV1,
  deliveryCancelRequestedV1,
  deliveryCancelRecordedV1,
  deliveryLocationUpdatedV1,
  deliveryCostRecordedV1,
  deliveryCodeCheckedV1,
] as const;
